/**
 * ルールの画面で使い回す小さな部品。
 *
 * 状態（RUN / STOP）の見せ方を 1 か所に寄せているのは、公開に直結する唯一の
 * スイッチだからである。一覧と詳細で色や言い回しが違うと、どちらが本当かを
 * 読み手が判断することになる。
 */

import type { ReactNode } from "react";
import { Tag, type RuleState } from "visumo-data-agent-web-shared";

/** 捨てた理由 1 つぶん。API が持っている文言（label）をそのまま受け取る */
export type DropReason = { reason: string; label: string; count: number };

/** userErrors の 1 件。API の形と、画面が自分で立てる分をそろえる */
export type RuleUserError = {
  field?: readonly string[] | null;
  code: string;
  message: string;
};

export function RuleStateTag({ state }: { state: RuleState }) {
  return state === "RUN" ? (
    <Tag tone="ok">配信中</Tag>
  ) : (
    <Tag>止まっています</Tag>
  );
}

/** 断られた理由。フォームの入力欄に紐づけられないものはここでまとめて出す */
export function RuleErrors({
  title,
  errors,
  onReload,
}: {
  title: ReactNode;
  errors: readonly RuleUserError[];
  onReload?: () => void;
}) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <div className="vd-notice vd-notice--error" role="alert">
      <p style={{ marginTop: 0 }}>
        <strong>{title}</strong>
      </p>
      {errors.map((error) => (
        <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
          {error.message}
        </p>
      ))}
      {onReload === undefined ? null : (
        <p style={{ margin: "8px 0 0" }}>
          <button type="button" className="vd-button" onClick={onReload}>
            最新を読み込む
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * 答えを捨てた理由の内訳。
 *
 * **0 件のときに出すためにある。** 何も出さないと「壊れている」と読まれるが、
 * 材料から確かな問いが作れなければ作らないのが設計どおりの振る舞いである。
 * 理由の文言（label）は API が持っているものをそのまま出す（画面で訳し直すと、
 * 理由が増えたときに画面だけ古くなる）。
 */
export function DroppedReasons({
  droppedFaqs,
  reasons,
}: {
  droppedFaqs: number;
  reasons: readonly DropReason[];
}) {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <p className="vd-stat-note" style={{ margin: 0 }}>
        作った答えのうち {droppedFaqs.toLocaleString("ja-JP")} 件を捨てました。
      </p>
      <ul className="vd-job-reasons">
        {reasons.map((item) => (
          <li key={item.reason}>
            {item.label}: {item.count.toLocaleString("ja-JP")} 件
          </li>
        ))}
      </ul>
    </div>
  );
}
